<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Statistics</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Statistics page</p>
      </div>
      <v-btn
        color="primary"
        variant="tonal"
        prepend-icon="mdi-refresh"
        :loading="loading"
        @click="loadData"
      >
        Refresh Data
      </v-btn>
    </div>

    <!-- Summary Card -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Quote Submissions</p>
              <h3 class="text-h4 font-weight-bold">{{ totalQuoteSubmissions }}</h3>
            </div>
            <v-avatar color="primary" variant="tonal" size="56">
              <v-icon size="28">mdi-file-document-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-icon color="info" size="18" class="mr-1">mdi-information-outline</v-icon>
            <span class="text-caption text-medium-emphasis">
              {{ filterSummary }}
            </span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card class="stat-card pa-5">
          <div class="d-flex align-center justify-space-between">
            <div>
              <p class="text-caption text-uppercase font-weight-medium text-medium-emphasis mb-1">Unique Addons</p>
              <h3 class="text-h4 font-weight-bold">{{ addons.length }}</h3>
            </div>
            <v-avatar color="success" variant="tonal" size="56">
              <v-icon size="28">mdi-plus-box-multiple-outline</v-icon>
            </v-avatar>
          </div>
          <div class="mt-3 d-flex align-center">
            <v-icon color="success" size="18" class="mr-1">mdi-check-circle-outline</v-icon>
            <span class="text-caption text-medium-emphasis">Selected in quotes</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card class="mb-6 pa-4">
      <div class="mb-2">
        <h1 class="text-h5 font-weight-bold mb-1">Addons Popularity Filter</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Most popular addons by occasion, delivery type and category</p>
      </div>
      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-select
          v-model="selectedOccasion"
          :items="occasionOptions"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 240px;"
          prepend-inner-icon="mdi-calendar-star"
          @update:modelValue="loadData"
        ></v-select>
        <v-select
          v-model="selectedDelivery"
          :items="deliveryOptions"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 240px;"
          prepend-inner-icon="mdi-truck-outline"
          @update:modelValue="loadData"
        ></v-select>
        <v-select
          v-model="selectedMenu"
          :items="menuOptions"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 240px;"
          prepend-inner-icon="mdi-food-outline"
          @update:modelValue="loadData"
        ></v-select>
        <v-select
          v-model="selectedCategory"
          :items="categoryOptions"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 240px;"
          prepend-inner-icon="mdi-shape-outline"
        ></v-select>
        <v-btn
          variant="tonal"
          density="compact"
          prepend-icon="mdi-refresh"
          @click="resetFilters"
        >
          Reset
        </v-btn>
      </div>
    </v-card>

    <!-- Table -->
    <v-row>
      <v-col cols="12">
        <v-card class="pa-4">
          <v-card-title class="d-flex align-center text-subtitle-1 font-weight-bold pb-2">
            <v-icon class="mr-2" color="primary">mdi-table</v-icon>
            All Addons Breakdown
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="tableHeaders"
              :items="filteredAddons"
              :loading="loading"
              :items-per-page="20"
              density="comfortable"
              class="elevation-0"
            >
              <template v-slot:item.rank="{ index }">
                <v-chip
                  v-if="index < 3"
                  :color="['amber', 'blue-grey', 'brown'][index]"
                  size="small"
                  variant="flat"
                  class="font-weight-bold"
                >
                  #{{ index + 1 }}
                </v-chip>
                <span v-else class="text-medium-emphasis">{{ index + 1 }}</span>
              </template>

              <template v-slot:item.categoryName="{ item }">
                <v-chip size="small" variant="tonal" color="primary">
                  {{ item.categoryName }}
                </v-chip>
              </template>

              <template v-slot:item.quoteCount="{ item }">
                <div class="d-flex align-center">
                  <span class="font-weight-bold mr-2">{{ item.quoteCount }}</span>
                  <v-progress-linear
                    :model-value="maxCount > 0 ? (item.quoteCount / maxCount) * 100 : 0"
                    color="primary"
                    height="6"
                    rounded
                    style="max-width: 100px;"
                  ></v-progress-linear>
                </div>
              </template>

              <template v-slot:item.percentage="{ item }">
                <span class="text-medium-emphasis">
                  {{ totalQuoteSubmissions > 0 ? ((item.quoteCount / totalQuoteSubmissions) * 100).toFixed(1) : 0 }}%
                </span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- Menu Popularity Filter -->
    <v-card class="mb-6 pa-4 mt-8">
      <div class="mb-3">
        <h1 class="text-h5 font-weight-bold mb-1">Menu Popularity Filter</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Number of quotes per menu, filtered by occasion and delivery type</p>
      </div>
      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-select
          v-model="menuOccasion"
          :items="occasionOptions"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 240px;"
          prepend-inner-icon="mdi-calendar-star"
          @update:modelValue="loadMenuData"
        ></v-select>
        <v-select
          v-model="menuDelivery"
          :items="deliveryOptions"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 240px;"
          prepend-inner-icon="mdi-truck-outline"
          @update:modelValue="loadMenuData"
        ></v-select>
        <v-btn
          variant="tonal"
          density="compact"
          prepend-icon="mdi-refresh"
          @click="resetMenuFilters"
        >
          Reset
        </v-btn>
      </div>
    </v-card>

    <!-- Menu Breakdown Table -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="pa-4">
          <v-card-title class="d-flex align-center justify-space-between text-subtitle-1 font-weight-bold pb-2">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-food-outline</v-icon>
              Quotes by Menu
            </div>
            <span class="text-caption text-medium-emphasis font-weight-regular">
              Total: {{ menuTotal }} quotes
            </span>
          </v-card-title>
          <v-card-text>
            <div v-if="menuLoading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="!menuBreakdown.length" class="text-center py-8 text-medium-emphasis">
              No data available for selected filters
            </div>
            <v-table v-else density="comfortable">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Menu</th>
                  <th class="text-right">Quotes</th>
                  <th class="text-right">%</th>
                  <th style="width: 160px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(menu, index) in menuBreakdown" :key="menu.menuName">
                  <td>
                    <v-chip
                      v-if="index < 3"
                      :color="['amber', 'blue-grey', 'brown'][index]"
                      size="small"
                      variant="flat"
                      class="font-weight-bold"
                    >#{{ index + 1 }}</v-chip>
                    <span v-else class="text-medium-emphasis">{{ index + 1 }}</span>
                  </td>
                  <td class="font-weight-medium">{{ menu.menuName }}</td>
                  <td class="text-right font-weight-bold">{{ menu.count }}</td>
                  <td class="text-right text-medium-emphasis">{{ menu.percentage }}%</td>
                  <td>
                    <v-progress-linear
                      :model-value="Number(menu.percentage)"
                      color="primary"
                      height="6"
                      rounded
                    ></v-progress-linear>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Cart Popularity Filter -->
    <v-card class="mb-6 pa-4 mt-8">
      <div class="mb-3">
        <h1 class="text-h5 font-weight-bold mb-1">Cart Popularity</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Number of quotes per cart, filtered by occasion</p>
      </div>
      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-select
          v-model="cartOccasion"
          :items="occasionOptions"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          style="max-width: 240px;"
          prepend-inner-icon="mdi-calendar-star"
          @update:modelValue="loadCartData"
        ></v-select>
        <v-btn
          variant="tonal"
          density="compact"
          prepend-icon="mdi-refresh"
          @click="resetCartFilters"
        >
          Reset
        </v-btn>
        <v-spacer></v-spacer>
        <span class="text-caption text-medium-emphasis">
          Total carts chosen: {{ cartTotal }}
        </span>
      </div>
    </v-card>

    <!-- Cart Breakdown Table -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="pa-4">
          <v-card-title class="d-flex align-center justify-space-between text-subtitle-1 font-weight-bold pb-2">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-cart-outline</v-icon>
              Quotes by Cart
            </div>
            <span class="text-caption text-medium-emphasis font-weight-regular">
              Total: {{ cartTotal }} quotes
            </span>
          </v-card-title>
          <v-card-text>
            <div v-if="cartLoading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="!carts.length" class="text-center py-8 text-medium-emphasis">
              No data available for selected filters
            </div>
            <v-table v-else density="comfortable">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Cart</th>
                  <th class="text-right">Quotes</th>
                  <th class="text-right">%</th>
                  <th style="width: 160px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(cart, index) in carts" :key="cart.name">
                  <td>
                    <v-chip
                      v-if="index < 3"
                      :color="['amber', 'blue-grey', 'brown'][index]"
                      size="small"
                      variant="flat"
                      class="font-weight-bold"
                    >#{{ index + 1 }}</v-chip>
                    <span v-else class="text-medium-emphasis">{{ index + 1 }}</span>
                  </td>
                  <td class="font-weight-medium">{{ cart.name }}</td>
                  <td class="text-right font-weight-bold">{{ cart.quoteCount }}</td>
                  <td class="text-right text-medium-emphasis">
                    {{ cartTotal > 0 ? ((cart.quoteCount / cartTotal) * 100).toFixed(1) : 0 }}%
                  </td>
                  <td>
                    <v-progress-linear
                      :model-value="cartTotal > 0 ? (cart.quoteCount / cartTotal) * 100 : 0"
                      color="primary"
                      height="6"
                      rounded
                    ></v-progress-linear>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { statisticsService } from "../services";
import { useNotificationStore } from "../stores";
import { getOccasionOptions } from "../constants/occasions";

export default {
  name: "StatisticsView",
  setup() {
    const notificationStore = useNotificationStore();
    return { notificationStore };
  },
  data() {
    return {
      loading: false,
      selectedOccasion: "all",
      selectedDelivery: "all",
      selectedCategory: "all",
      selectedMenu: "all",
      menuNames: [],
      addons: [],
      totalQuoteSubmissions: 0,
      // Menu popularity section
      menuLoading: false,
      menuOccasion: "all",
      menuDelivery: "all",
      menuBreakdown: [],
      menuTotal: 0,
      // Cart popularity section
      cartLoading: false,
      cartOccasion: "all",
      carts: [],
      cartTotal: 0,
      tableHeaders: [
        { title: "#", key: "rank", sortable: false, width: "60px" },
        { title: "Addon Name", key: "addonName", sortable: true },
        { title: "Category", key: "categoryName", sortable: true },
        { title: "Quote Submissions", key: "quoteCount", sortable: true },
        { title: "% of Total", key: "percentage", sortable: false },
      ],
    };
  },
  computed: {
    occasionOptions() {
      return [
        { title: "All Occasions", value: "all" },
        ...getOccasionOptions().map((o) => ({ title: o.title, value: o.title })),
      ];
    },
    deliveryOptions() {
      return [
        { title: "All Delivery Types", value: "all" },
        { title: "Drop off per person", value: "Drop off per person" },
        { title: "Pickup per person", value: "Pickup per person" },
        { title: "On-site cooking", value: "On-site cooking" },
      ];
    },
    deliveryLabel() {
      const opt = this.deliveryOptions.find((o) => o.value === this.selectedDelivery);
      return opt?.title || this.selectedDelivery;
    },
    filterSummary() {
      const parts = [];
      parts.push(this.selectedOccasion === "all" ? "All occasions" : this.selectedOccasion);
      if (this.selectedDelivery !== "all") parts.push(this.deliveryLabel);
      return parts.join(" / ");
    },
    menuOptions() {
      return [
        { title: "All Menus", value: "all" },
        ...this.menuNames.map((name) => ({ title: name, value: name })),
      ];
    },
    categoryOptions() {
      const categories = [...new Set(this.addons.map((a) => a.categoryName))].sort();
      return [
        { title: "All Categories", value: "all" },
        ...categories.map((c) => ({ title: c, value: c })),
      ];
    },
    filteredAddons() {
      if (this.selectedCategory === "all") return this.addons;
      return this.addons.filter((a) => a.categoryName === this.selectedCategory);
    },
    maxCount() {
      if (this.filteredAddons.length === 0) return 0;
      return Math.max(...this.filteredAddons.map((a) => a.quoteCount));
    },
  },
  methods: {
    async loadMenuData() {
      this.menuLoading = true;
      try {
        const response = await statisticsService.getQuotesByMenu({
          occasion: this.menuOccasion,
          delivery: this.menuDelivery,
        });
        if (response.success) {
          this.menuBreakdown = response.data.menus;
          this.menuTotal = response.data.total;
        }
      } catch (error) {
        this.notificationStore.handleApiError(error, "Failed to load menu popularity data");
        this.menuBreakdown = [];
        this.menuTotal = 0;
      } finally {
        this.menuLoading = false;
      }
    },
    resetMenuFilters() {
      this.menuOccasion = "all";
      this.menuDelivery = "all";
      this.loadMenuData();
    },
    async loadCartData() {
      this.cartLoading = true;
      try {
        const response = await statisticsService.getCartsByOccasion({
          occasion: this.cartOccasion,
        });
        if (response.success) {
          this.carts = response.data.carts;
          this.cartTotal = response.data.totalQuoteSubmissions;
        }
      } catch (error) {
        this.notificationStore.handleApiError(error, "Failed to load cart popularity data");
        this.carts = [];
        this.cartTotal = 0;
      } finally {
        this.cartLoading = false;
      }
    },
    resetCartFilters() {
      this.cartOccasion = "all";
      this.loadCartData();
    },
    resetFilters() {
      this.selectedOccasion = "all";
      this.selectedDelivery = "all";
      this.selectedCategory = "all";
      this.selectedMenu = "all";
      this.loadData();
    },
    async loadMenuNames() {
      try {
        const response = await statisticsService.getMenuNames();
        if (response.success) {
          this.menuNames = response.data.menuNames;
        }
      } catch (error) {
        this.menuNames = [];
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const response = await statisticsService.getAddonsByOccasion({
          occasion: this.selectedOccasion,
          delivery: this.selectedDelivery,
          menu: this.selectedMenu,
        });
        if (response.success) {
          this.addons = response.data.addons;
          this.totalQuoteSubmissions = response.data.totalQuoteSubmissions;
        }
      } catch (error) {
        this.notificationStore.handleApiError(
          error,
          "Failed to load statistics data"
        );
        this.addons = [];
        this.totalQuoteSubmissions = 0;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadMenuNames();
    this.loadData();
    this.loadMenuData();
    this.loadCartData();
  },
};
</script>

<style scoped>
.stat-card {
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}
</style>
